/* Main-thread client for the ledger Web Worker (ledger.worker.js). One lazy
 * shared worker instance; each call gets a sequence id so replies and progress
 * events route back to the right caller. */

let worker = null;
let seq = 0;
const pending = new Map();

function getWorker() {
  if (!worker) {
    worker = new Worker(new URL("./ledger.worker.js", import.meta.url), { type: "module" });
    worker.onmessage = (event) => {
      const data = event.data || {};
      const job = pending.get(data.id);
      if (!job) return;
      if (data.type === "progress") {
        job.onProgress?.(data);
        return;
      }
      pending.delete(data.id);
      if (data.type === "error") job.reject(new Error(data.message));
      else job.resolve(data);
    };
    worker.onerror = (event) => {
      // A worker-level failure (e.g. it failed to load) strands every pending
      // job — fail them all so callers can fall back or surface the error.
      const error = new Error(event.message || "ledger worker failed");
      for (const job of pending.values()) job.reject(error);
      pending.clear();
    };
  }
  return worker;
}

function call(payload, { onProgress, transfer } = {}) {
  seq += 1;
  const id = seq;
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject, onProgress });
    getWorker().postMessage({ ...payload, id }, transfer || []);
  });
}

/* Parse a pasted/uploaded PGN or NDJSON export off the main thread. Returns
 * { games, username, site, detected } — when `unameHint` matches nothing (or
 * is empty), the worker detects the account the export belongs to. */
export function parseImportInWorker(text, unameHint, onProgress) {
  return call({ type: "parse-import", text, uname: unameHint }, { onProgress });
}

/* games -> gzipped JSON chunks (ArrayBuffer[], each < 1 MiB) for Firestore. */
export async function packGames(games) {
  const { chunks, gameCount } = await call({ type: "pack", games });
  return { chunks, gameCount };
}

/* gzipped chunks (Uint8Array[]/ArrayBuffer[]) -> games. */
export async function unpackChunks(chunks) {
  const { games } = await call({ type: "unpack", chunks });
  return games;
}

/* Run the "Your Mistakes" miner over the account's full cached history.
 * Cheap after the first run — per-game digests are cached in IndexedDB.
 *
 * `cap` is how many cards the ranked deck is cut to; the worker defaults it.
 * See learn/mistakes.js#deckCap for who gets which. */
export function mineMistakes(account, onProgress, watch = {}, cap) {
  return call({ type: "mine", account, watch, cap }, { onProgress });
}
